import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Project } from '../../store/slices/projectsSlice';
import { api } from '../../services/api';
import ProjectCardCarousel from '../UI/ProjectCardCarousel';

interface ProjectCardProps {
  project: Project;
}

interface ProjectFile {
  id?: string;
  _id?: string;
  type: 'image' | 'video' | 'document';
  url: string;
  thumbnailUrl?: string;
  description?: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const navigate = useNavigate();
  const [imageFiles, setImageFiles] = useState<ProjectFile[]>([]);

  useEffect(() => {
    const fetchImageFiles = async () => {
      if (!project._id && !project.id) return;
      
      try {
        const projectId = project._id?.toString() || project.id?.toString();
        if (!projectId) return;

        const files = await api.projectFiles.getProjectFiles(projectId, false);
        const images = files.filter((file: any) => file.type === 'image' && !file.deletedAt);
        setImageFiles(images);
      } catch (error) {
        console.error('Error fetching project images:', error);
      }
    };

    fetchImageFiles();
  }, [project._id, project.id]);

  // Find active voting session
  const findActiveVotingSession = () => {
    if (!project.votingSessions || project.votingSessions.length === 0) {
      return null;
    }

    const now = new Date();
    const activeSession = project.votingSessions.find((session: any) => {
      const startDate = new Date(session.startDate);
      const endDate = new Date(session.endDate);
      return session.status === 'active' && now >= startDate && now <= endDate;
    });

    return activeSession || null;
  };

  // Find nearest scheduled voting session
  const findNearestScheduledSession = () => {
    if (!project.votingSessions || project.votingSessions.length === 0) {
      return null;
    }

    const now = new Date();
    const scheduledSessions = project.votingSessions
      .filter((session: any) => {
        const startDate = new Date(session.startDate);
        return session.status === 'scheduled' && startDate > now;
      })
      .sort((a: any, b: any) => {
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      });

    return scheduledSessions.length > 0 ? scheduledSessions[0] : null;
  };

  const activeVotingSession = findActiveVotingSession();
  const scheduledVotingSession = findNearestScheduledSession();

  const handleVoteClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const projectId = project._id?.toString() || project.id;
    navigate(`/projects/${projectId}`, { state: { scrollToVoting: true } });
  };

  const statusColors: Record<string, string> = {
    draft: 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-800)]',
    planned: 'bg-[var(--color-brand-100)] text-[var(--color-brand-800)]',
    scheduled: 'bg-[var(--color-warning-100)] text-[var(--color-warning-800)]',
    active: 'bg-[var(--color-success-100)] text-[var(--color-success-800)]',
    vote_session: 'bg-[var(--color-brand-200)] text-[var(--color-brand-900)]',
    executed: 'bg-[var(--color-brand-100)] text-[var(--color-brand-800)]',
    canceled: 'bg-[var(--color-error-100)] text-[var(--color-error-800)]',
    suspended: 'bg-[var(--color-warning-100)] text-[var(--color-warning-800)]',
    failed: 'bg-[var(--color-error-100)] text-[var(--color-error-800)]',
  };

  // Calculate priority support percentage (mock for now)
  const getPrioritySupport = () => {
    const mockPercentages: Record<string, number> = {
      'agriculture': 57,
      'solar': 43,
      'coastal': 40,
    };
    const category = project.category?.toLowerCase() || '';
    return mockPercentages[category] || Math.floor(Math.random() * 30) + 30;
  };

  const prioritySupport = getPrioritySupport();
  const hasVotingResults = project.stats?.votes && project.stats.votes > 0;

  // Get benefits from goals or use defaults
  const getBenefits = () => {
    // This would come from project goals or metadata
    const category = project.category?.toLowerCase() || '';
    const benefitMap: Record<string, string[]> = {
      'agriculture': ['Supports local farmers', 'Reduces food imports'],
      'solar': ['Improves health resilience', 'Provides energy backup'],
      'coastal': ['Protects marine ecosystems', 'Reduces coastal erosion'],
    };
    return benefitMap[category] || ['Community benefit', 'Local impact'];
  };

  const benefits = getBenefits();

  return (
    <div className="border border-[var(--color-neutral-border)] rounded-lg overflow-hidden hover:shadow-lg transition-shadow bg-[var(--color-default-background)] relative">
      {/* Priority Support Banner */}
      {hasVotingResults === true  && (
        <div className="absolute top-0 left-0 bg-[var(--color-warning-500)] text-white px-3 py-1 text-xs font-semibold z-10 rounded-br-lg">
          Approved by Citizens {prioritySupport}% Priority Support
        </div>
      )}

      <Link to={`/projects/${project._id?.toString() || project.id}`}>
        <div className="w-full">
          <ProjectCardCarousel
            images={imageFiles.map((file) => {
              const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
              const url = file.url.startsWith('http') ? file.url : `${apiBaseUrl}${file.url}`;
              const thumbnailUrl = file.thumbnailUrl;
              
              return {
                id: file.id || file._id,
                url,
                thumbnailUrl,
                description: file.description,
                alt: file.description || project.title,
              };
            })}
            scheduledVotingSession={scheduledVotingSession}
            activeVotingSession={activeVotingSession}
            projectTitle={project.title}
            className="w-full"
            onVoteClick={handleVoteClick}
          />
        </div>
      </Link>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-semibold text-[var(--color-default-font)] flex-1 pr-2">
            {project.title}
          </h3>
          <span className="px-2 py-1 text-xs font-medium rounded bg-[var(--color-success-100)] text-[var(--color-success-800)] whitespace-nowrap">
            Eligible
          </span>
        </div>

        <p className="text-[var(--color-subtext-color)] text-sm mb-4 line-clamp-3">
          {project.description || 'This initiative aims to benefit the community through strategic improvements and sustainable development.'}
        </p>

        {/* Benefits */}
        {benefits.length > 0 && (
          <ul className="space-y-1 mb-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-[var(--color-default-font)]">
                <span className="text-[var(--color-success-600)]">✓</span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-[var(--color-neutral-border)]">
          <div className="flex items-center gap-2 text-xs text-[var(--color-subtext-color)]">
            <span className="capitalize">{project.category}</span>
            {project.geographicRegion?.parish && (
              <>
                <span>•</span>
                <span>{project.geographicRegion.parish}</span>
              </>
            )}
          </div>
          <Link
            to={`/projects/${project._id?.toString() || project.id}`}
            className="px-4 py-2 bg-[var(--color-success-600)] text-white rounded-lg hover:bg-[var(--color-success-700)] transition-colors text-sm font-medium"
          >
            LEARN MORE
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
